import { Autocomplete, Box, Button, Card, FormControlLabel, Grid, Switch, TextField, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { apiConstants, postGeneric } from '../../common/constants'
import * as React from 'react'
import _ from "lodash";
import MUIModal from "../../components/MuiModal";
import { fillPreviewData } from "./labelPreview";
import { useSnackbar } from "notistack";
import Loader from "../../common/Loader";
import { useForm } from "react-hook-form";
import { FormProvider, RHFFileField, RHFSearchableSelectField, RHFSelectField } from "../../common/hook-form";
import { LoadingButton } from "@mui/lab";
import { useGetAllActiveMerchantsIdAndNameCachedQuery, useGetAllCourierPartnersCachedQuery } from "../../redux/commonApiCalls";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RHFMultipleSelectField from "../../common/hook-form/RHFMultipleSelectField";
import SideDrawer from "../../components/SideDrawer";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";

// ── Rich-cell / chip helpers (Forward-Shipment table look) ────────────────────
const NAVY = '#212B36', MUTED = '#637381';
const Pill = ({ children, bg, fg }) => (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', height: 22, px: 1, borderRadius: '6px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.02em', color: fg, bgcolor: bg, whiteSpace: 'nowrap' }}>{children}</Box>
);
const typeTone = (t) => (String(t).toUpperCase() === 'MERCHANT' ? { bg: '#e3f2fd', fg: '#1565c0' } : { bg: '#f3e5f5', fg: '#6a1b9a' });
const isTruthy = (v) => v === true || ['yes', 'true', '1'].includes(String(v).toLowerCase());
// Stable colour for an owner's initial-avatar.
const AV_PALETTE = ['#5E8E3E', '#0F766E', '#B45309', '#6D28D9', '#2874F0', '#DB2777'];
const avatarColor = (s) => AV_PALETTE[[...(s || '?')].reduce((a, c) => a + c.charCodeAt(0), 0) % AV_PALETTE.length];
const downloadTemplate = (t) => {
    const blob = new Blob([t.template], { type: 'text/html' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `template_${t.owner}_${t.type}.html`;
    document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url); document.body.removeChild(a);
};
// Dropdown menu with a hairline separator under each option (matches the merchant/courier picker).
const sepMenuProps = { PaperProps: { sx: { '& .MuiList-root': { py: 0 }, '& .MuiMenuItem-root': { borderBottom: '1px solid #eef1f5' }, '& .MuiMenuItem-root:last-of-type': { borderBottom: 'none' } } } };

function LabelConfig({ showUploadOnly = false }) {
    const [reload, setReload] = useState(0);
    const [data, setData] = useState();
    const [loading, setLoading] = useState(false)
    const [ownerFilter, setOwnerFilter] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [open, setOpen] = useState(false)            // full-view modal
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [demoData, setDemoData] = useState(true);   // preview with realistic demo values vs raw template
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        setLoading(true)
        const payload = {
            "collection": "shipping_label_templates",
            limit: 10000
        }
        postGeneric(apiConstants.REPORT_GENERIC_SEARCH, payload).then((res) => {
            setData(_.sortBy(res?.data?.data, 'owner'));
            setLoading(false)
        }).catch((err) => {
            enqueueSnackbar("Some error occurred!", { variant: 'error' })
            console.log(err)
            setLoading(false)
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reload])

    const handleClose = () => setOpen(false);
    const handleOpen = (temp) => { setSelectedTemplate(temp); setOpen(true); };

    // Distinct owners that actually have a template (merged merchant + courier from the generic API).
    const ownerOptions = useMemo(() => {
        const m = {};
        (data || []).forEach((t) => {
            const type = String(t.type || '').toUpperCase();
            const key = `${t.owner}|${type}`;
            if (!m[key]) m[key] = { owner: t.owner || '—', type, fullOwnerName: t.fullOwnerName || '', count: 0 };
            m[key].count += 1;
        });
        return _.sortBy(Object.values(m), ['type', 'owner']);
    }, [data]);

    // List = every template, optionally narrowed to the picked owner.
    const list = useMemo(() => {
        const rows = (data || []).map((t, i) => ({ ...t, _key: `${t.owner}|${t.type}|${t.size}|${i}` }));
        if (!ownerFilter) return rows;
        return rows.filter((t) => (t.owner || '—') === ownerFilter.owner && String(t.type || '').toUpperCase() === ownerFilter.type);
    }, [data, ownerFilter]);

    const grouped = useMemo(() => {
        const g = {};
        list.forEach((t) => { const o = t.owner || '—'; (g[o] = g[o] || []).push(t); });
        return g;
    }, [list]);

    // Keep a valid selection as the search narrows the list.
    useEffect(() => {
        if (list.length === 0) { setSelectedRow(null); return; }
        setSelectedRow((cur) => (cur && list.some((t) => t._key === cur._key)) ? cur : list[0]);
    }, [list]);
    if (showUploadOnly) {
        return (<div><UploadLabel setReload={setReload} /></div>);
    }

    return (
        <div>
            {loading && <Loader />}

            {/* Toolbar — search + upload (upload lives in a drawer, not a big form) */}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                <Autocomplete
                    options={ownerOptions}
                    value={ownerFilter}
                    onChange={(e, v) => setOwnerFilter(v)}
                    groupBy={(o) => (o.type === 'MERCHANT' ? 'Merchants' : 'Couriers')}
                    getOptionLabel={(o) => o.owner || ''}
                    isOptionEqualToValue={(a, b) => a.owner === b.owner && a.type === b.type}
                    sx={{ flex: 1, minWidth: 260, bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: '10px', py: 0.25 } }}
                    renderInput={(params) => (
                        <TextField {...params} size="small" placeholder="Select a merchant or courier with templates…"
                            InputProps={{ ...params.InputProps, startAdornment: <SearchIcon sx={{ fontSize: 18, color: '#8A94A6', ml: 0.5 }} /> }} />
                    )}
                    renderOption={(props, o) => (
                        <Box component="li" {...props} key={`${o.owner}|${o.type}`} sx={{ gap: 1 }}>
                            <Box sx={{ width: 24, height: 24, borderRadius: '6px', flex: '0 0 auto', display: 'grid', placeItems: 'center', color: '#fff', fontSize: 11, fontWeight: 800, bgcolor: avatarColor(o.owner) }}>{(o.owner || '?')[0]?.toUpperCase()}</Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: NAVY, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.owner}</Typography>
                                {o.fullOwnerName && <Typography sx={{ fontSize: 11, color: MUTED, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.fullOwnerName}</Typography>}
                            </Box>
                            <Pill {...typeTone(o.type)}>{o.type}</Pill>
                            <Pill bg="#eef1f5" fg="#475569">{o.count}</Pill>
                        </Box>
                    )}
                />
                {ownerFilter && (
                    <Button variant="text" onClick={() => setOwnerFilter(null)} sx={{ textTransform: 'none', fontWeight: 600, color: MUTED }}>
                        Clear
                    </Button>
                )}
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setUploadOpen(true)}
                    sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#1565c0', boxShadow: 'none', '&:hover': { bgcolor: '#0d47a1', boxShadow: 'none' } }}>
                    Upload Template
                </Button>
            </Box>

            {/* Master-detail library */}
            <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <Card sx={{ borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', height: { md: 640 }, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #E7EAF0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 800, color: NAVY }}>Templates</Typography>
                            <Pill bg="#eef1f5" fg="#475569">{list.length}</Pill>
                        </Box>
                        <Box sx={{ flex: 1, overflowY: 'auto', p: 0.75 }}>
                            {list.length === 0 ? (
                                <Box sx={{ p: 4, textAlign: 'center', color: MUTED, fontSize: 13 }}>No templates uploaded yet.</Box>
                            ) : (
                                Object.entries(grouped).map(([owner, items]) => (
                                    <Box key={owner}>
                                        <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: '#8A94A6', px: 1.25, pt: 1.25, pb: 0.5 }}>{owner}</Typography>
                                        {items.map((t) => {
                                            const sel = selectedRow && selectedRow._key === t._key;
                                            return (
                                                <Box key={t._key} onClick={() => setSelectedRow(t)}
                                                    sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.25, py: 1, borderRadius: '9px', cursor: 'pointer',
                                                        bgcolor: sel ? '#E8F1FC' : 'transparent', boxShadow: sel ? 'inset 3px 0 0 #1565c0' : 'none',
                                                        '&:hover': { bgcolor: sel ? '#E8F1FC' : '#F1F3F7' } }}>
                                                    <Box sx={{ width: 30, height: 30, borderRadius: '8px', flex: '0 0 auto', display: 'grid', placeItems: 'center', color: '#fff', fontSize: 12, fontWeight: 800, bgcolor: avatarColor(owner) }}>{(owner || '?')[0]?.toUpperCase()}</Box>
                                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: NAVY, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.owner || '—'}</Typography>
                                                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25, alignItems: 'center', flexWrap: 'wrap' }}>
                                                            {t.type && <Pill {...typeTone(t.type)}>{t.type}</Pill>}
                                                            {t.size && <Pill bg="#eef1f5" fg="#475569">{t.size}</Pill>}
                                                            {isTruthy(t.isDefault) && <Pill bg="#e7f6ef" fg="#1b7f5c">Default</Pill>}
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                ))
                            )}
                        </Box>
                    </Card>
                </Grid>

                <Grid item xs={12} md={8}>
                    <Card sx={{ borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', height: { md: 640 }, display: 'flex', flexDirection: 'column' }}>
                        {selectedRow ? (
                            <>
                                <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #E7EAF0', display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography sx={{ fontSize: 15, fontWeight: 800, color: NAVY }}>{selectedRow.owner || '—'}</Typography>
                                        {selectedRow.fullOwnerName && <Typography sx={{ fontSize: 12, color: MUTED }}>{selectedRow.fullOwnerName}</Typography>}
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                        {selectedRow.type && <Pill {...typeTone(selectedRow.type)}>{selectedRow.type}</Pill>}
                                        {selectedRow.size && <Pill bg="#eef1f5" fg="#475569">{selectedRow.size}</Pill>}
                                        {isTruthy(selectedRow.isDefault) && <Pill bg="#e7f6ef" fg="#1b7f5c">Default</Pill>}
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
                                        {selectedRow.createdDate && <Typography sx={{ fontSize: 11, color: MUTED }}>Created: {new Date(selectedRow.createdDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</Typography>}
                                        {selectedRow.lastModifiedDate && <Typography sx={{ fontSize: 11, color: MUTED }}>Modified: {new Date(selectedRow.lastModifiedDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</Typography>}
                                        {selectedRow.modifiedByUser && <Typography sx={{ fontSize: 11, color: MUTED }}>By: {selectedRow.modifiedByUser}</Typography>}
                                    </Box>
                                    {selectedRow.template && (
                                        <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
                                            <FormControlLabel
                                                control={<Switch size="small" checked={demoData} onChange={(e) => setDemoData(e.target.checked)} />}
                                                label="Demo data"
                                                sx={{ mr: 0.5, '& .MuiFormControlLabel-label': { fontSize: 12, color: MUTED } }}
                                            />
                                            <Button size="small" variant="outlined" startIcon={<VisibilityIcon fontSize="small" />} onClick={() => handleOpen(selectedRow.template)} sx={{ textTransform: 'none', fontWeight: 600 }}>Full view</Button>
                                            <Button size="small" variant="contained" startIcon={<DownloadIcon fontSize="small" />} onClick={() => downloadTemplate(selectedRow)} sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#1565c0', boxShadow: 'none', '&:hover': { bgcolor: '#0d47a1', boxShadow: 'none' } }}>Download</Button>
                                        </Box>
                                    )}
                                </Box>
                                <Box sx={{ flex: 1, bgcolor: '#eef1f5', display: 'flex', justifyContent: 'center', overflow: 'auto', p: 2.5 }}>
                                    {selectedRow.template
                                        ? <Box component="iframe" title="Template preview" srcDoc={demoData ? fillPreviewData(selectedRow.template) : selectedRow.template} sx={{ border: 'none', width: '100%', maxWidth: 400, height: 560, bgcolor: '#fff', borderRadius: '6px', boxShadow: '0 6px 20px rgba(15,23,42,0.14)' }} />
                                        : <Box sx={{ alignSelf: 'center', color: MUTED, fontSize: 13 }}>No template file to preview.</Box>}
                                </Box>
                            </>
                        ) : (
                            <Box sx={{ flex: 1, display: 'grid', placeItems: 'center', color: MUTED, fontSize: 13 }}>Select a template to preview.</Box>
                        )}
                    </Card>
                </Grid>
            </Grid>

            {/* Upload drawer */}
            <SideDrawer open={uploadOpen} setOpen={() => setUploadOpen(false)} title="Upload Label Template" subtitle="Upload a new label template for a merchant or courier" drawerWidth="50%">
                <UploadLabel setReload={setReload} />
            </SideDrawer>

            {/* Full-view modal — snug to a realistic A6 label, centred on a backdrop */}
            <MUIModal open={open} maxWidth="sm" handleClose={handleClose}>
                {selectedTemplate && (
                    <Box sx={{ py: 1, bgcolor: '#eef1f5', display: 'flex', justifyContent: 'center' }}>
                        <Box component="iframe" title="Template full preview" srcDoc={demoData ? fillPreviewData(selectedTemplate) : selectedTemplate}
                            sx={{ width: '100%', height: '80vh', border: 'none', borderRadius: '6px', bgcolor: '#fff', boxShadow: '0 8px 28px rgba(15,23,42,0.18)' }} />
                    </Box>
                )}
            </MUIModal>
        </div>
    );
}
export default LabelConfig;



const UploadLabel = ({ setReload }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [isLoading, setIsLoading] = useState(false)
    const andfilter = [{ "field": "is_deleted", "operator": "eq", "value": '0' }];
    const filter = {
        "andfilter": andfilter,
        "offset": 0,
        "limit": 1000,
        // "controllerFilter": true,
        "skipCount": true,
        "includes": "id,name,cpAccountCode,prozoId,supportedShipmentType,isBlended,parent"
        // "excludes": exclude,
    }
    const { data: courier } = useGetAllCourierPartnersCachedQuery(filter);
    const { data: merchants = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery()
    const sortCourier = _.sortBy(courier, 'parent')
    const sortMerchant = _.sortBy(merchants, 'name')

    const methods = useForm({
        defaultValues: {
            courier: '',
            merchant: '',
            type: '',
            size: '',
            file: null
        }
    });
    const { handleSubmit, reset, watch } = methods
    const onSubmit = (data) => {
        setIsLoading(true)
        const formData = new FormData();
        formData.append('file', data?.file[0]);
        formData.append("type", data.type);
        formData.append("size", data.size);
        formData.append("courierIds", data.courier);
        if (data.merchant) {
            formData.append("merchantId", data.merchant);
        }
        postGeneric(apiConstants.UPLOAD_LABEL_TEMPLATE_MULTIPLECOURIER, formData).then((res) => {
            if (res.status === 200) {
                enqueueSnackbar('Successfully uploaded!', { variant: 'success' })
            }
            setReload(reload => reload + 1)
            reset()
            setIsLoading(false)
        }).catch((err) => {
            console.log(err);
            enqueueSnackbar(err.response?.data?.message || 'Some error occurred', { variant: 'error' })
            setIsLoading(false)
        })
    }
    const SelectedType = watch('type')


    // const handleClear = async () => {
    //     setIsLoading(true);
    //     try {
    //         const [cacheRes] = await Promise.all([
    //             deleteGeneric(`${apiConstants.GET_CACHE}keyval_30MinTTL`),
    //             deleteGeneric(`${apiConstants.GET_CACHE}InMem-MerchantConfigCache`),
    //             deleteGeneric(`${apiConstants.GET_CACHE}keyval_6hrTTL`),
    //         ]);


    //     } catch (error) {
    //         console.error(error);
    //         enqueueSnackbar(error?.message || 'Something went wrong', { variant: 'error' });
    //     } finally {
    //         setIsLoading(false);
    //     }
    // };

    const typeChosen = SelectedType === "MERCHANT" || SelectedType === "COURIER";

    return (
        <Box sx={{ width: '100%' }}>
            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                {/* Step 1 — who is this template for */}
                <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: '#8A94A6', mb: 1.25 }}>Template owner</Typography>
                <Grid container spacing={2.5}>
                    <Grid item xs={12} md={6}>
                        <RHFSelectField name="type" label="Owner type" width='100%' color="secondary" MenuProps={sepMenuProps} options={[
                            { label: "Merchant", value: "MERCHANT" },
                            { label: "Courier", value: "COURIER" },
                        ]}
                        />
                    </Grid>
                    {SelectedType === "MERCHANT" && (
                        <Grid item xs={12} md={6}>
                            <RHFSearchableSelectField
                                width="100%"
                                name="merchant"
                                label={`Select Merchant`}
                                options={
                                    sortMerchant.map((val) => ({
                                        value: val.id,
                                        label: val.name,
                                    }))
                                }
                            />
                        </Grid>
                    )}
                </Grid>

                {typeChosen && (
                    <>
                        {/* Step 2 — courier mapping + label file */}
                        <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: '#8A94A6', mt: 3, mb: 1.25 }}>Courier &amp; label</Typography>
                        <Grid container spacing={2.5}>
                            <Grid item xs={12} md={6}>
                                <RHFMultipleSelectField
                                    width="100%"
                                    name="courier"
                                    label="Select Courier"
                                    search={true}
                                    options={[
                                        { label: "All", value: "ALL" },
                                        ...sortCourier.map((val) => ({
                                            value: val.id,
                                            label: val.cpAccountCode + " - [" + val.parent + "]",
                                        })),
                                    ]}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <RHFSelectField name="size" label="Label size" width='100%' color="secondary" MenuProps={sepMenuProps} options={[
                                    { label: "A6", value: "A6" },
                                    { label: "A4", value: "A4" },
                                    { label: "A4_LANDSCAPE", value: "A4_LANDSCAPE" },
                                    { label: "A6X4", value: "A6X4" },
                                ]}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: NAVY, mb: 0.75 }}>Template file (HTML)</Typography>
                                <RHFFileField name='file' required={true} />
                            </Grid>
                        </Grid>
                    </>
                )}

                {/* Action footer */}
                <Box sx={{ display: 'flex', gap: 1.5, mt: 3.5, pt: 2.5, borderTop: '1px solid #E7EAF0' }}>
                    <LoadingButton loading={isLoading} type="submit" variant="contained"
                        sx={{ textTransform: 'none', fontWeight: 700, px: 3, bgcolor: '#1565c0', boxShadow: 'none', '&:hover': { bgcolor: '#0d47a1', boxShadow: 'none' } }}>
                        Upload template
                    </LoadingButton>
                    <Button variant="outlined" color="inherit" onClick={() => reset()}
                        sx={{ textTransform: 'none', fontWeight: 600, borderColor: '#DCE1E8', color: MUTED }}>
                        Reset
                    </Button>
                </Box>
            </FormProvider>
        </Box>
    )
}